/**
 * Omnichannel Page - Unified messaging view
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useOmnichannelSocket } from '../hooks/useOmnichannelSocket';
import { useAuth } from '../providers/AuthProvider';
import { useCompanyProject } from '../providers/CompanyProjectProvider';
import { omnichannelApi } from '../services/omnichannelApi';
import {
  ClientContactInfo,
  ConversationPreview,
  getSourceColor,
  getSourceIcon,
  OmnichannelMessage,
} from '../types/omnichannel';

export const OmnichannelPage: React.FC = () => {
  const { user } = useAuth();
  const { currentProject } = useCompanyProject();

  // State
  const [conversations, setConversations] = useState<ConversationPreview[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<ConversationPreview | null>(null);
  const [messages, setMessages] = useState<OmnichannelMessage[]>([]);
  const [contacts, setContacts] = useState<ClientContactInfo[]>([]);
  const [selectedContactId, setSelectedContactId] = useState<number | null>(null);
  const [messageText, setMessageText] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Draft editing state
  const [editingDraftId, setEditingDraftId] = useState<number | null>(null);
  const [editingDraftText, setEditingDraftText] = useState('');

  // Client name editing state
  const [isEditingName, setIsEditingName] = useState(false);
  const [editingName, setEditingName] = useState('');
  const [isSavingName, setIsSavingName] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Refs for stable callbacks
  const selectedConversationRef = useRef<ConversationPreview | null>(null);
  selectedConversationRef.current = selectedConversation;

  // Load conversations
  const loadConversations = useCallback(async () => {
    try {
      const response = await omnichannelApi.getConversations({
        project_id: currentProject?.id,
        query: searchQuery || undefined,
        size: 50,
      });
      setConversations(response.conversations);
    } catch (err) {
      console.error('Failed to load conversations:', err);
      setError('Failed to load conversations');
    }
  }, [searchQuery, currentProject]);

  // Load messages for selected conversation
  const loadMessages = useCallback(async (conv: ConversationPreview) => {
    try {
      const response = await omnichannelApi.getConversationMessages(conv.client_id, {
        size: 100,
        peer_address: conv.peer_address || undefined
      });
      setMessages(response.messages);

      // Only load contacts for real clients (not orphan messages)
      if (conv.client_id > 0) {
        const clientContacts = await omnichannelApi.getClientContacts(conv.client_id);
        setContacts(clientContacts);

        // Set default contact: last message's contact or primary
        if (clientContacts.length > 0) {
          const lastMessage = response.messages[response.messages.length - 1];
          if (lastMessage?.client_contact_id) {
            setSelectedContactId(lastMessage.client_contact_id);
          } else {
            const primary = clientContacts.find(c => c.is_primary) || clientContacts[0];
            setSelectedContactId(primary.id);
          }
        }
      } else {
        // Orphan messages - no contacts
        setContacts([]);
        setSelectedContactId(null);
      }
    } catch (err) {
      console.error('Failed to load messages:', err);
      setError('Failed to load messages');
    }
  }, []);

  // Store functions in refs for stable WebSocket callbacks
  const loadConversationsRef = useRef(loadConversations);
  const loadMessagesRef = useRef(loadMessages);
  useEffect(() => {
    loadConversationsRef.current = loadConversations;
    loadMessagesRef.current = loadMessages;
  }, [loadConversations, loadMessages]);

  // Helper to check if event matches selected conversation
  const isEventForSelectedConversation = (event: any, conv: ConversationPreview | null): boolean => {
    if (!conv) return false;

    // Normalize client_id: backend sends null, frontend uses 0 for orphan messages
    const eventClientId = event.client_id ?? 0;
    const convClientId = conv.client_id ?? 0;

    // Match by client_id for regular clients
    if (eventClientId > 0 && convClientId > 0) {
      return eventClientId === convClientId;
    }

    // For orphan messages (client_id = 0 or null), match by peer_address
    if (eventClientId === 0 && convClientId === 0) {
      return event.peer_address === conv.peer_address;
    }

    return false;
  };

  // WebSocket callbacks (stable - no dependencies that change)
  const socketCallbacks = React.useMemo(() => ({
    onNewMessage: (event: any) => {
      loadConversationsRef.current();
      const conv = selectedConversationRef.current;
      if (isEventForSelectedConversation(event, conv)) {
        loadMessagesRef.current(conv!);
      }
    },
    onDraftCreated: (event: any) => {
      loadConversationsRef.current();
      const conv = selectedConversationRef.current;
      if (isEventForSelectedConversation(event, conv)) {
        loadMessagesRef.current(conv!);
      }
    },
    onMessageUpdated: () => {
      loadConversationsRef.current();
      const conv = selectedConversationRef.current;
      if (conv) {
        loadMessagesRef.current(conv);
      }
    },
    onDraftSent: () => {
      loadConversationsRef.current();
      const conv = selectedConversationRef.current;
      if (conv) {
        loadMessagesRef.current(conv);
      }
    },
  }), []);

  // WebSocket connection
  const { isConnected, isSubscribed, error: socketError } = useOmnichannelSocket(socketCallbacks);

  // Initial load
  useEffect(() => {
    setIsLoading(true);
    loadConversations().finally(() => setIsLoading(false));
  }, [loadConversations]);

  // Reload on search change (debounced)
  useEffect(() => {
    const timer = setTimeout(() => {
      loadConversations();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, loadConversations]);

  // Auto-select the most recent conversation when conversations are loaded and none is selected
  useEffect(() => {
    // Only auto-select if:
    // 1. Conversations are loaded (not loading)
    // 2. There are conversations available
    // 3. No conversation is currently selected
    // 4. Not searching (to avoid auto-selecting during search)
    if (!isLoading && conversations.length > 0 && !selectedConversation && !searchQuery) {
      // Find the conversation with the most recent last_message_at
      const mostRecentConversation = conversations.reduce((latest, current) => {
        if (!latest.last_message_at) return current;
        if (!current.last_message_at) return latest;
        
        const latestDate = new Date(latest.last_message_at).getTime();
        const currentDate = new Date(current.last_message_at).getTime();
        
        return currentDate > latestDate ? current : latest;
      });

      // Auto-select the most recent conversation and load its messages
      if (mostRecentConversation) {
        setSelectedConversation(mostRecentConversation);
        setEditingDraftId(null);
        setIsEditingName(false);
        setEditingName('');
        loadMessages(mostRecentConversation);
      }
    }
  }, [conversations, isLoading, selectedConversation, searchQuery, loadMessages]);

  // Select a conversation
  const selectConversation = async (conv: ConversationPreview) => {
    setSelectedConversation(conv);
    setEditingDraftId(null);
    setIsEditingName(false);
    setEditingName('');
    await loadMessages(conv);
  };

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Send message
  const handleSendMessage = async () => {
    if (!messageText.trim() || !selectedConversation || !selectedContactId) return;

    setIsSending(true);
    try {
      await omnichannelApi.sendMessage({
        client_id: selectedConversation.client_id,
        client_contact_id: selectedContactId,
        body_text: messageText.trim(),
      });
      setMessageText('');
      await loadMessages(selectedConversation);
      loadConversations();
    } catch (err) {
      console.error('Failed to send message:', err);
      setError('Failed to send message');
    } finally {
      setIsSending(false);
    }
  };

  // Draft actions
  const handleEditDraft = (draft: OmnichannelMessage) => {
    setEditingDraftId(draft.id);
    setEditingDraftText(draft.body_text);
  };

  const handleSaveDraft = async () => {
    if (!editingDraftId || !editingDraftText.trim()) return;

    try {
      await omnichannelApi.updateDraft(editingDraftId, { body_text: editingDraftText.trim() });
      setEditingDraftId(null);
      setEditingDraftText('');
      if (selectedConversation) {
        await loadMessages(selectedConversation);
      }
    } catch (err) {
      console.error('Failed to update draft:', err);
      setError('Failed to update draft');
    }
  };

  const handleDeleteDraft = async (draftId: number) => {
    if (!window.confirm('Are you sure you want to delete this draft?')) return;

    try {
      await omnichannelApi.deleteDraft(draftId);
      if (selectedConversation) {
        await loadMessages(selectedConversation);
      }
      loadConversations();
    } catch (err) {
      console.error('Failed to delete draft:', err);
      setError('Failed to delete draft');
    }
  };

  const handleSendDraft = async (draftId: number) => {
    try {
      await omnichannelApi.sendDraft(draftId, {
        client_contact_id: selectedContactId || undefined,
      });
      if (selectedConversation) {
        await loadMessages(selectedConversation);
      }
      loadConversations();
    } catch (err) {
      console.error('Failed to send draft:', err);
      setError('Failed to send draft');
    }
  };

  // Client name editing handlers
  const handleStartEditingName = () => {
    if (!selectedConversation) return;
    const currentName = getClientDisplayName(
      selectedConversation.client_name,
      selectedConversation.client_company,
      selectedConversation.peer_address
    );
    setEditingName(currentName === 'unknown' ? '' : currentName);
    setIsEditingName(true);
  };

  const handleCancelEditingName = () => {
    setIsEditingName(false);
    setEditingName('');
  };

  const handleSaveClientName = async () => {
    if (!selectedConversation || !editingName.trim()) return;

    setIsSavingName(true);
    try {
      // Determine source from the conversation
      const source = selectedConversation.sources[0] || 'unknown';

      const result = await omnichannelApi.updateClientName(selectedConversation.client_id, {
        name: editingName.trim(),
        peer_address: selectedConversation.peer_address || undefined,
        source: selectedConversation.client_id === 0 ? source : undefined,
      });

      // Update the selected conversation with new data
      setSelectedConversation((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          client_id: result.id,
          client_name: result.name,
          client_company: result.company,
        };
      });

      setIsEditingName(false);
      setEditingName('');
      loadConversations(); // Refresh the list
    } catch (err) {
      console.error('Failed to save client name:', err);
      setError('Failed to save client name');
    } finally {
      setIsSavingName(false);
    }
  };

  // Get selected conversation from state (already stored when selected)

  return (
    <div className="h-[calc(100vh-4rem)] flex bg-slate-50">
      {/* Left Panel - Conversation List */}
      <div className="w-80 border-r border-slate-200 bg-white flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-slate-200">
          <h1 className="text-xl font-semibold text-slate-800 mb-3">Messages</h1>

          {/* Connection status */}
          <div className="flex items-center gap-2 mb-3 text-xs">
            <span className={`w-2 h-2 rounded-full ${isConnected ? 'bg-emerald-500' : 'bg-red-500'}`} />
            <span className="text-slate-500">
              {isConnected ? (isSubscribed ? 'Live updates' : 'Connecting...') : 'Disconnected'}
            </span>
          </div>

          {/* Search */}
          <input
            type="text"
            placeholder="Search clients..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          />
        </div>

        {/* Conversation List */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="p-4 text-center text-slate-500">Loading...</div>
          ) : conversations.length === 0 ? (
            <div className="p-4 text-center text-slate-500">No conversations</div>
          ) : (
            conversations.map((conv) => (
              <div
                key={conv.client_id > 0 ? `client-${conv.client_id}` : `peer-${conv.peer_address}`}
                onClick={() => selectConversation(conv)}
                className={`p-4 border-b border-slate-100 cursor-pointer hover:bg-slate-50 transition-colors ${selectedConversation?.client_id === conv.client_id &&
                  selectedConversation?.peer_address === conv.peer_address
                  ? 'bg-indigo-50 border-l-4 border-l-indigo-500' : ''
                  }`}
              >
                <div className="flex justify-between items-start mb-1">
                  <h3 className="font-medium text-slate-800 truncate">
                    {getClientDisplayName(conv.client_name, conv.client_company, conv.peer_address)}
                  </h3>
                  {conv.last_message_at && (
                    <span className="text-xs text-slate-400 flex-shrink-0 ml-2">
                      {formatDateTime(conv.last_message_at)}
                    </span>
                  )}
                </div>

                {conv.client_company && conv.client_name && (
                  <p className="text-xs text-slate-500 mb-1">{conv.client_company}</p>
                )}

                <div className="flex items-center gap-2 mb-1">
                  {/* Source icons */}
                  <div className="flex gap-1">
                    {conv.sources.map((source) => (
                      <span key={source} title={source} className="text-sm">
                        {getSourceIcon(source)}
                      </span>
                    ))}
                  </div>

                  {/* Draft badge */}
                  {conv.draft_count > 0 && (
                    <span className="px-1.5 py-0.5 text-xs bg-amber-100 text-amber-700 rounded">
                      {conv.draft_count} draft{conv.draft_count > 1 ? 's' : ''}
                    </span>
                  )}
                </div>

                {conv.last_message_text && (
                  <p className="text-sm text-slate-500 truncate">
                    {conv.last_message_direction === 'outgoing' && '↩ '}
                    {conv.last_message_text}
                  </p>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Right Panel - Message Thread */}
      <div className="flex-1 flex flex-col bg-white">
        {selectedConversation ? (
          <>
            {/* Conversation Header */}
            <div className="p-4 border-b border-slate-200 bg-white">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  {isEditingName ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleSaveClientName();
                          if (e.key === 'Escape') handleCancelEditingName();
                        }}
                        placeholder="Enter client name..."
                        autoFocus
                        className="flex-1 max-w-xs px-3 py-1.5 text-lg font-semibold border border-indigo-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                      <button
                        onClick={handleSaveClientName}
                        disabled={!editingName.trim() || isSavingName}
                        className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {isSavingName ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        onClick={handleCancelEditingName}
                        disabled={isSavingName}
                        className="px-3 py-1.5 text-sm bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 disabled:opacity-50 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <h2
                      onClick={handleStartEditingName}
                      className="text-lg font-semibold text-slate-800 cursor-pointer hover:text-indigo-600 transition-colors"
                      title="Click to edit name"
                    >
                      {getClientDisplayName(
                        selectedConversation.client_name,
                        selectedConversation.client_company,
                        selectedConversation.peer_address
                      )}
                      <span className="ml-2 text-slate-400 text-sm">✎</span>
                    </h2>
                  )}
                  {selectedConversation?.client_company && selectedConversation?.client_name && (
                    <p className="text-sm text-slate-500">{selectedConversation.client_company}</p>
                  )}
                </div>
                <div className="flex gap-2">
                  {selectedConversation?.sources.map((source) => (
                    <span
                      key={source}
                      className={`px-2 py-1 text-xs rounded-full ${getSourceColor(source)}`}
                    >
                      {getSourceIcon(source)} {source}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50">
              {messages.map((message) => (
                <MessageBubble
                  key={message.id}
                  message={message}
                  isEditing={editingDraftId === message.id}
                  editingText={editingDraftText}
                  onEditingTextChange={setEditingDraftText}
                  onEdit={() => handleEditDraft(message)}
                  onSave={handleSaveDraft}
                  onCancel={() => setEditingDraftId(null)}
                  onDelete={() => handleDeleteDraft(message.id)}
                  onSend={() => handleSendDraft(message.id)}
                />
              ))}
              <div ref={messagesEndRef} />
            </div>

            {/* Message Input */}
            <div className="p-4 border-t border-slate-200 bg-white">
              {/* Channel selector - only show if multiple contacts */}
              {contacts.length > 1 && (
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-sm text-slate-500">Send via:</span>
                  <select
                    value={selectedContactId || ''}
                    onChange={(e) => setSelectedContactId(Number(e.target.value))}
                    className="text-sm border border-slate-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    {contacts.map((contact) => (
                      <option key={contact.id} value={contact.id}>
                        {getSourceIcon(contact.source)} {contact.source}: {contact.peer_address}
                        {contact.is_primary ? ' (primary)' : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="flex gap-3">
                <textarea
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                  placeholder="Type a message..."
                  rows={2}
                  className="flex-1 px-4 py-2 border border-slate-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!messageText.trim() || isSending || !selectedContactId}
                  className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                >
                  {isSending ? 'Sending...' : 'Send'}
                </button>
              </div>
            </div>
          </>
        ) : (
          /* Empty State */
          <div className="flex-1 flex items-center justify-center text-slate-400">
            <div className="text-center">
              <svg className="w-16 h-16 mx-auto mb-4 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              <p className="text-lg font-medium">Select a conversation</p>
              <p className="text-sm mt-1">Choose a client from the list to view messages</p>
            </div>
          </div>
        )}
      </div>

      {/* Error Toast */}
      {(error || socketError) && (
        <div className="fixed bottom-4 right-4 bg-red-500 text-white px-4 py-3 rounded-lg shadow-lg flex items-center gap-3">
          <span>{error || socketError}</span>
          <button
            onClick={() => setError(null)}
            className="text-white hover:text-red-100"
          >
            ✕
          </button>
        </div>
      )}
    </div>
  );
};

// Message bubble component
interface MessageBubbleProps {
  message: OmnichannelMessage;
  isEditing: boolean;
  editingText: string;
  onEditingTextChange: (text: string) => void;
  onEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
  onDelete: () => void;
  onSend: () => void;
}

const MessageBubble: React.FC<MessageBubbleProps> = ({
  message,
  isEditing,
  editingText,
  onEditingTextChange,
  onEdit,
  onSave,
  onCancel,
  onDelete,
  onSend,
}) => {
  const isIncoming = message.direction === 'incoming';
  const isDraft = message.is_draft;

  return (
    <div className={`flex ${isIncoming ? 'justify-start' : 'justify-end'}`}>
      <div
        className={`max-w-[70%] rounded-2xl px-4 py-3 ${isDraft
          ? 'bg-amber-50 border-2 border-dashed border-amber-300'
          : isIncoming
            ? 'bg-white border border-slate-200'
            : 'bg-indigo-600 text-white'
          }`}
      >
        {/* Source badge */}
        <div className={`flex items-center gap-2 mb-1 text-xs ${isIncoming ? 'text-slate-500' : isDraft ? 'text-amber-600' : 'text-indigo-200'
          }`}>
          <span className={`px-1.5 py-0.5 rounded ${getSourceColor(message.source)}`}>
            {getSourceIcon(message.source)} {message.source}
          </span>
          {isDraft && (
            <span className="px-1.5 py-0.5 bg-amber-200 text-amber-700 rounded">
              Draft
            </span>
          )}
          <span>{formatDateTime(message.created_at)}</span>
        </div>

        {/* Subject - only show for email and if different from body */}
        {shouldShowSubject(message.source, message.subject, message.body_text) && (
          <div className={`text-sm font-medium mb-1 ${isIncoming ? 'text-slate-700' : isDraft ? 'text-amber-800' : 'text-white'
            }`}>
            {message.subject}
          </div>
        )}

        {/* Content */}
        {isEditing ? (
          <textarea
            value={editingText}
            onChange={(e) => onEditingTextChange(e.target.value)}
            className="w-full p-2 border border-slate-200 rounded-lg text-slate-800 text-sm resize-none"
            rows={4}
          />
        ) : (
          <div className={`text-sm whitespace-pre-wrap ${isIncoming ? 'text-slate-800' : isDraft ? 'text-amber-900' : 'text-white'
            }`}>
            {message.body_text}
          </div>
        )}

        {/* Draft actions */}
        {isDraft && (
          <div className="flex gap-2 mt-3 pt-2 border-t border-amber-200">
            {isEditing ? (
              <>
                <button
                  onClick={onSave}
                  className="px-3 py-1 text-xs bg-emerald-500 text-white rounded hover:bg-emerald-600 transition-colors"
                >
                  Save
                </button>
                <button
                  onClick={onCancel}
                  className="px-3 py-1 text-xs bg-slate-200 text-slate-700 rounded hover:bg-slate-300 transition-colors"
                >
                  Cancel
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={onEdit}
                  className="px-3 py-1 text-xs bg-slate-200 text-slate-700 rounded hover:bg-slate-300 transition-colors"
                >
                  Edit
                </button>
                <button
                  onClick={onSend}
                  className="px-3 py-1 text-xs bg-indigo-500 text-white rounded hover:bg-indigo-600 transition-colors"
                >
                  Send
                </button>
                <button
                  onClick={onDelete}
                  className="px-3 py-1 text-xs bg-red-100 text-red-600 rounded hover:bg-red-200 transition-colors"
                >
                  Delete
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Helper to format date and time (converts UTC to local time)
function formatDateTime(isoString: string): string {
  const date = new Date(isoString);
  const now = new Date();
  const isToday = date.toLocaleDateString() === now.toLocaleDateString();

  const time = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  if (isToday) {
    return time;
  }

  const day = date.getDate();
  const month = date.toLocaleDateString('en-US', { month: 'short' });
  const year = date.getFullYear();
  return `${day} ${month} ${year}, ${time}`;
}

// Helper to check if string is a valid email address
function isEmail(str: string): boolean {
  // Email regex: local@domain.tld format
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(str);
}

// Helper to check if string is a phone number
function isPhoneNumber(str: string): boolean {
  // Phone patterns: starts with + followed by digits, or just digits with optional dashes/spaces
  const cleaned = str.replace(/[\s\-\(\)]/g, '');
  return /^\+?\d{7,15}$/.test(cleaned);
}

// Helper to check if string looks like a technical identifier (UUID, telegram:xxx, etc.)
function isTechnicalIdentifier(str: string | null | undefined): boolean {
  if (!str) return true;
  // UUID pattern
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str)) return true;
  // Source prefix pattern (telegram:xxx, whatsapp:xxx, etc.)
  if (/^(telegram|whatsapp|email|slack|facebook|phone|http|websocket):/i.test(str)) return true;
  // Pure numeric ID
  if (/^\d+$/.test(str)) return true;
  return false;
}

// Helper to get display name for a conversation
function getClientDisplayName(
  clientName: string | null | undefined,
  clientCompany: string | null | undefined,
  peerAddress: string | null | undefined
): string {
  // Check client_name first (if it's a real name, not a technical ID)
  if (clientName && !isTechnicalIdentifier(clientName)) {
    return clientName;
  }
  // Check client_company
  if (clientCompany && !isTechnicalIdentifier(clientCompany)) {
    return clientCompany;
  }
  // Check peer_address - only show if it's email or phone
  if (peerAddress && (isEmail(peerAddress) || isPhoneNumber(peerAddress))) {
    return peerAddress;
  }
  return 'unknown';
}

// Helper to check if subject should be displayed
// Subject should only be shown for email channels, and only if it's different from body_text
function shouldShowSubject(source: string, subject: string | null, bodyText: string): boolean {
  if (!subject || !subject.trim()) {
    return false;
  }

  // Extract base channel from source (e.g., "websocket:example.com" -> "websocket")
  const baseSource = source.includes(':') ? source.split(':')[0] : source;

  // Only email channels typically have meaningful subjects
  if (baseSource !== 'email') {
    return false;
  }

  // Don't show subject if it's identical or very similar to body_text
  const normalizedSubject = subject.trim().toLowerCase();
  const normalizedBody = bodyText.trim().toLowerCase();

  // If subject is the same as body (or body starts with subject), don't show it
  if (normalizedSubject === normalizedBody || normalizedBody.startsWith(normalizedSubject + ' ')) {
    return false;
  }

  return true;
}


